/**
 * Local-time date helpers for the money module.
 *
 * `src/lib/api.ts` has toISOString()-based helpers, which answer in UTC — at
 * 9pm in Charlotte that is already tomorrow, which would date a receipt wrong
 * and roll the period over a day early. Money screens use the family's local
 * calendar day, so these read the local date parts directly.
 */

/** Today, local, as YYYY-MM-DD. */
export function todayISO(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The current month, local, as YYYY-MM. */
export function currentPeriod(d = new Date()): string {
  return todayISO(d).slice(0, 7);
}

/** Shift a YYYY-MM period by n months. */
export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}

/** "2026-07" -> "July 2026". */
export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** "2026-07" -> "Jul". */
export function periodShortLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** "2026-07-12" -> "Sun Jul 12". */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** The n most recent periods, newest first, always including `include`. */
export function recentPeriods(count: number, include: string): string[] {
  const now = currentPeriod();
  const list: string[] = [];
  for (let i = 0; i < count; i++) list.push(shiftPeriod(now, -i));
  if (!list.includes(include)) {
    list.push(include);
    list.sort((a, b) => b.localeCompare(a));
  }
  return list;
}

export function isValidPeriod(period: string | null | undefined): period is string {
  return !!period && /^\d{4}-(0[1-9]|1[0-2])$/.test(period);
}
